import { RefObject } from "react";
import {
  Control,
  Path,
  UseFormClearErrors,
  UseFormWatch,
} from "react-hook-form";
import { useTranslation } from "@/hooks/useTranslation";

import NativeSelect from "@/components/ui/input/native-select";
import type { CheckoutFormData } from "@/components/checkout/checkout.types";
import { CountriesAndLanguages } from "@/networking/addresses.api";
import { State } from "@/lib/country-config";
import { AddressField } from "@/networking/addresses.api";
import { AddressSuggestion } from "./hooks/useAutocomplete";
import DynamicAddressFields from "@/components/shared/dynamic-address-fields";

function Loader() {
  return (
    <div className="flex animate-pulse flex-col gap-4">
      <div className="h-12 w-full rounded bg-gray-200"></div>
      <div className="h-12 w-full rounded bg-gray-200"></div>
      <div className="h-12 w-full rounded bg-gray-200"></div>
      <div className="h-12 w-full rounded bg-gray-200"></div>
    </div>
  );
}

interface AddressFormProps {
  // Core form control
  control: Control<CheckoutFormData>;
  watch: UseFormWatch<CheckoutFormData>;

  // NEW: Dynamic field path
  fieldPath: Path<CheckoutFormData>;

  // Data
  countries: CountriesAndLanguages["countries"];
  stateOptions: State[];
  addressFields: AddressField[];

  // Event handlers (passed from parent)
  onCountryChange: (countryCode: string, countryName: string) => void;

  // Autocomplete (passed from parent)
  renderAutocompleteOptions: (isFocused: boolean) => React.ReactNode;
  getAutocompleteOptions: (input: string, formCountry: string) => void;
  handleOptionClick: (option: AddressSuggestion, formCountry: string) => void;

  // UI
  title?: string;
  networkError?: string;
  isLoading?: boolean;
  ref?: RefObject<HTMLDivElement>;
  className?: string;
  roundedTop?: boolean;
  roundedBottom?: boolean;
  collectName?: boolean;
  clearErrors?: UseFormClearErrors<CheckoutFormData>;
}

export default function AddressForm({
  control,
  countries,
  watch,
  fieldPath,
  stateOptions,
  addressFields,
  onCountryChange,
  renderAutocompleteOptions,
  getAutocompleteOptions,
  handleOptionClick,
  ref,
  networkError,
  title,
  isLoading = false,
  className,
  roundedTop = true,
  collectName = true,
  clearErrors,
}: AddressFormProps) {
  const { t } = useTranslation();
  const formCountry = watch(
    `${fieldPath}.country` as Path<CheckoutFormData>,
  ) as string;
  const formCountryCode = watch(
    `${fieldPath}.country_code` as Path<CheckoutFormData>,
  ) as string;
  const countryCodeFieldPath =
    `${fieldPath}.country_code` as Path<CheckoutFormData>;

  return (
    <div ref={ref ? ref : undefined} className={className}>
      <form>
        <legend className="block text-sm/6 font-medium text-gray-900">
          {title}
        </legend>
        {networkError && (
          <div
            className="text-sm text-red-500"
            data-testid="address-error-inline"
          >
            {networkError}
          </div>
        )}
        {isLoading ? (
          <Loader />
        ) : (
          <div className="my-2 rounded shadow-xs">
            <NativeSelect
              key={formCountryCode || "no-country"}
              control={control}
              label={t("country")}
              placeholder={t("select_an_option")}
              options={countries?.map((country) => ({
                id: country.iso,
                name: country.name,
              }))}
              name={countryCodeFieldPath}
              onSelectChange={(e) => {
                onCountryChange(e.id.toString(), e.name);
              }}
              inputStyles={roundedTop ? "rounded-t" : ""}
            />
            <DynamicAddressFields
              addressFields={addressFields}
              stateOptions={stateOptions}
              fieldPathPrefix={fieldPath}
              control={control}
              formCountry={formCountry}
              countryCode={formCountryCode}
              renderAutocompleteOptions={renderAutocompleteOptions}
              getAutocompleteOptions={getAutocompleteOptions}
              handleOptionClick={handleOptionClick}
              collectName={collectName}
              clearErrors={clearErrors}
            />
          </div>
        )}
      </form>
    </div>
  );
}
